<!-- src/components/common/SessionWarningModal.vue -->
<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isVisible"
        class="session-warning-overlay"
        @click.self="handleExtend"
      >
        <div class="session-warning-modal" role="alertdialog" aria-modal="true">
          <!-- 헤더 -->
          <div class="modal-header bg-warning-subtle border-warning">
            <i class="bi bi-clock-history text-warning me-2 fs-4"></i>
            <h5 class="modal-title mb-0">세션 만료 경고</h5>
          </div>

          <!-- 본문 -->
          <div class="modal-body text-center py-4">
            <!-- 카운트다운 원형 표시 -->
            <div class="countdown-circle mb-3">
              <svg viewBox="0 0 100 100" class="countdown-svg">
                <circle
                  class="countdown-bg"
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="#e9ecef"
                  stroke-width="8"
                />
                <circle
                  class="countdown-progress"
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="8"
                  stroke-linecap="round"
                  :stroke-dasharray="circumference"
                  :stroke-dashoffset="progressOffset"
                  :class="urgencyClass"
                />
              </svg>
              <div class="countdown-text">
                <span class="countdown-time" :class="urgencyClass">
                  {{ remainingFormatted }}
                </span>
              </div>
            </div>

            <p class="mb-2">
              <strong>{{ remainingMinutes }}분 {{ remainingSeconds }}초</strong>
              후 자동으로 로그아웃됩니다.
            </p>
            <p class="text-muted small mb-0">
              계속 사용하시려면 <strong>세션 연장</strong> 버튼을 클릭하세요.
            </p>
          </div>

          <!-- 푸터 -->
          <div class="modal-footer justify-content-center gap-2">
            <button
              type="button"
              class="btn btn-outline-secondary"
              @click="handleLogout"
            >
              <i class="bi bi-box-arrow-right me-1"></i>
              지금 로그아웃
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="handleExtend"
              ref="extendBtnRef"
            >
              <i class="bi bi-arrow-clockwise me-1"></i>
              세션 연장
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
/* ==============================
 * 1) Imports
 * ============================== */
import { ref, computed, watch, nextTick } from "vue";

/* ==============================
 * 2) Props / Emits
 * ============================== */
const props = defineProps({
  /** 모달 표시 여부 */
  isVisible: {
    type: Boolean,
    default: false,
  },
  /** 남은 시간 (ms) */
  remainingTime: {
    type: Number,
    default: 0,
  },
  /** 전체 경고 시간 (ms) - 진행률 계산용 */
  totalWarningTime: {
    type: Number,
    default: 5 * 60 * 1000, // 5분
  },
});

const emit = defineEmits(["extend", "logout"]);

/* ==============================
 * 3) Refs
 * ============================== */
const extendBtnRef = ref(null);

/* ==============================
 * 4) Computed
 * ============================== */

// 남은 시간 (분/초)
const remainingMinutes = computed(() =>
  Math.floor(props.remainingTime / 60000)
);

const remainingSeconds = computed(() =>
  Math.floor((props.remainingTime % 60000) / 1000)
);

// MM:SS 포맷
const remainingFormatted = computed(() => {
  const min = String(remainingMinutes.value).padStart(2, "0");
  const sec = String(remainingSeconds.value).padStart(2, "0");
  return `${min}:${sec}`;
});

// SVG 원형 진행률
const circumference = 2 * Math.PI * 45; // r=45

const progressOffset = computed(() => {
  const progress = props.remainingTime / props.totalWarningTime;
  return circumference * (1 - Math.max(0, Math.min(1, progress)));
});

// 긴급도에 따른 클래스
const urgencyClass = computed(() => {
  const remaining = props.remainingTime;
  if (remaining <= 30 * 1000) return "text-danger"; // 30초 이하
  if (remaining <= 60 * 1000) return "text-warning"; // 1분 이하
  return "text-primary";
});

/* ==============================
 * 5) Watchers
 * ============================== */

// 모달 표시 시 버튼에 포커스
watch(
  () => props.isVisible,
  async (visible) => {
    if (visible) {
      await nextTick();
      extendBtnRef.value?.focus();
    }
  }
);

/* ==============================
 * 6) Methods
 * ============================== */

function handleExtend() {
  emit("extend");
}

function handleLogout() {
  emit("logout");
}
</script>

<style scoped>
/* 오버레이 */
.session-warning-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
}

/* 모달 */
.session-warning-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;
  overflow: hidden;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* 헤더 */
.modal-header {
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid;
}

/* 카운트다운 원형 */
.countdown-circle {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto;
}

.countdown-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.countdown-progress {
  transition:
    stroke-dashoffset 1s linear,
    color 0.3s;
}

.countdown-text {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.countdown-time {
  font-size: 1.75rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* 푸터 */
.modal-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid #dee2e6;
}

/* 트랜지션 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 반응형 */
@media (max-width: 480px) {
  .session-warning-modal {
    width: 95%;
    margin: 1rem;
  }

  .countdown-circle {
    width: 100px;
    height: 100px;
  }

  .countdown-time {
    font-size: 1.5rem;
  }
}
</style>
